/**
 * Twilight Theme Configuration
 *
 * A blue-tinted dark theme with indigo accents and subtle glow effects.
 * Inspired by modern dashboard designs with immersive, oceanic backgrounds.
 *
 * Key differences from other dark themes:
 * - Blue-tinted backgrounds (#0a1019 vs #0a0a0a or #0A1929)
 * - Indigo primary accent (#6366F1)
 * - Slate-tinted text colors
 * - Cool, professional feel
 */

import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { baseColors, twilightPalette } from './palette';
import { darkShadows, glassEffects, glowEffects } from './shadows';
import { muiTransitions, transitionPresets } from './transitions';
import { typographyVariants } from './typography';

// ============================================================================
// Twilight-Specific Effects
// ============================================================================

const twilightGlass = glassEffects.twilight;

// ============================================================================
// Twilight Theme Options
// ============================================================================

const twilightThemeOptions: ThemeOptions = {
  palette: twilightPalette,
  typography: typographyVariants,
  transitions: muiTransitions,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    darkShadows[1],
    darkShadows[2],
    darkShadows[2],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[6],
  ],
  components: {
    // ========================================================================
    // CssBaseline
    // ========================================================================
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: twilightPalette.background.default,
          scrollbarColor: 'rgba(148, 163, 184, 0.2) transparent',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(148, 163, 184, 0.2)',
            borderRadius: 4,
            '&:hover': {
              background: 'rgba(148, 163, 184, 0.3)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Button
    // ========================================================================
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          textTransform: 'none',
          transition: transitionPresets.button,
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.MuiButton-contained.MuiButton-colorPrimary': {
            background: `linear-gradient(135deg, ${baseColors.teal[400]} 0%, ${baseColors.teal[500]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColors.teal[300]} 0%, ${baseColors.teal[400]} 100%)`,
              boxShadow: `${darkShadows[3]}, ${glowEffects.teal.strong}`,
            },
          },
          '&.MuiButton-contained.MuiButton-colorSecondary': {
            background: `linear-gradient(135deg, ${baseColors.secondary[400]} 0%, ${baseColors.secondary[500]} 100%)`,
            '&:hover': {
              boxShadow: `${darkShadows[3]}, ${glowEffects.secondary.strong}`,
            },
          },
        },
        contained: {
          boxShadow: darkShadows[2],
          '&:hover': {
            boxShadow: `${darkShadows[3]}, ${glowEffects.teal.medium}`,
          },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: 'rgba(148, 163, 184, 0.2)',
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            borderWidth: 1.5,
            borderColor: 'rgba(148, 163, 184, 0.4)',
            backgroundColor: 'rgba(148, 163, 184, 0.05)',
          },
        },
        text: {
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.08)',
          },
        },
      },
    },

    // ========================================================================
    // Card (Twilight Glass)
    // ========================================================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: twilightGlass.background,
          backdropFilter: twilightGlass.backdropBlur,
          border: `1px solid ${twilightGlass.border}`,
          boxShadow: twilightGlass.shadow,
          transition: transitionPresets.card,
          overflow: 'visible',
          '&:hover': {
            background: twilightGlass.backgroundHover,
            border: `1px solid ${twilightGlass.borderHover}`,
            boxShadow: `${twilightGlass.shadowHover}, ${glowEffects.teal.subtle}`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiCardActionArea-focusHighlight': {
            backgroundColor: 'rgba(148, 163, 184, 0.5)',
            opacity: 0,
            transition: transitionPresets.allFast,
          },
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0.04,
          },
          '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
            opacity: 0.08,
          },
        },
      },
    },

    // ========================================================================
    // Paper (Twilight Surface)
    // ========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: twilightPalette.background.paper,
        },
        elevation1: {
          boxShadow: darkShadows[1],
        },
        elevation2: {
          boxShadow: darkShadows[2],
        },
        elevation3: {
          boxShadow: darkShadows[3],
        },
        elevation4: {
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // TextField / Input
    // ========================================================================
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'transparent',
          transition: transitionPresets.input,
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.2)',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
            borderColor: baseColors.teal[400],
            boxShadow: `0 0 0 3px rgba(15, 181, 174, 0.2)`,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(148, 163, 184, 0.1)',
          transition: transitionPresets.input,
        },
        input: {
          backgroundColor: 'transparent',
          '&::placeholder': {
            color: 'rgba(148, 163, 184, 0.5)',
            opacity: 1,
          },
          // Override browser autofill styling
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
            caretColor: 'rgba(255, 255, 255, 0.9)',
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '&:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(148, 163, 184, 0.7)',
          '&.Mui-focused': {
            color: baseColors.teal[400],
          },
        },
      },
    },

    // ========================================================================
    // Chip
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(148, 163, 184, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.15)',
            boxShadow: glowEffects.teal.subtle,
          },
        },
        outlined: {
          borderColor: 'rgba(148, 163, 184, 0.2)',
        },
      },
    },

    // ========================================================================
    // Dialog / Modal (Twilight Glass)
    // ========================================================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'rgba(15, 22, 33, 0.98)',
          backdropFilter: 'blur(24px)',
          boxShadow: `${darkShadows[5]}, ${glowEffects.teal.medium}`,
          border: '1px solid rgba(148, 163, 184, 0.1)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px 24px 16px',
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: 12,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(10, 16, 25, 0.8)',
            backdropFilter: 'blur(8px)',
          },
        },
      },
    },

    // ========================================================================
    // Menu / Dropdown
    // ========================================================================
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          background: 'rgba(15, 22, 33, 0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: darkShadows[4],
          border: '1px solid rgba(148, 163, 184, 0.1)',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '12px 16px',
          minHeight: 44,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(15, 181, 174, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(15, 181, 174, 0.2)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Tooltip
    // ========================================================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: baseColors.neutral[900],
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.75rem',
          fontWeight: 500,
          boxShadow: darkShadows[3],
        },
        arrow: {
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },

    // ========================================================================
    // Avatar
    // ========================================================================
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '2px solid rgba(148, 163, 184, 0.1)',
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Drawer (Sidebar)
    // ========================================================================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: twilightPalette.background.default,
          borderRight: '1px solid rgba(148, 163, 184, 0.08)',
          borderRadius: 0,
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // AppBar (Twilight Glass)
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: darkShadows[1],
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(10, 16, 25, 0.85)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
        },
      },
    },

    // ========================================================================
    // FormControlLabel — add breathing room between control and label (switch, checkbox, radio)
    // ========================================================================
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: 12,
        },
      },
    },

    // ========================================================================
    // Switch
    // ========================================================================
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: baseColors.indigo[400],
              opacity: 1,
            },
          },
        },
        track: {
          borderRadius: 22,
          backgroundColor: 'rgba(148, 163, 184, 0.2)',
        },
        thumb: {
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Skeleton (Shimmer)
    // ========================================================================
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'rgba(148, 163, 184, 0.08)',
        },
      },
    },

    // ========================================================================
    // Tabs
    // ========================================================================
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: baseColors.teal[400],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          color: 'rgba(148, 163, 184, 0.7)',
          transition: transitionPresets.allFast,
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.9)',
          },
          '&.Mui-selected': {
            color: baseColors.teal[400],
          },
        },
      },
    },

    // ========================================================================
    // List
    // ========================================================================
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(15, 181, 174, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(15, 181, 174, 0.2)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Divider
    // ========================================================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(148, 163, 184, 0.08)',
        },
      },
    },

    // ========================================================================
    // Table
    // ========================================================================
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(148, 163, 184, 0.03)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(148, 163, 184, 0.08)',
        },
        head: {
          fontWeight: 600,
          color: 'rgba(148, 163, 184, 0.8)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.03)',
          },
        },
      },
    },

    // ========================================================================
    // Alert
    // ========================================================================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
          '&.MuiAlert-standard.MuiAlert-colorSuccess': {
            backgroundColor: 'rgba(16, 185, 129, 0.12)',
            borderColor: 'rgba(16, 185, 129, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorError': {
            backgroundColor: 'rgba(244, 63, 94, 0.12)',
            borderColor: 'rgba(244, 63, 94, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorWarning': {
            backgroundColor: 'rgba(245, 158, 11, 0.12)',
            borderColor: 'rgba(245, 158, 11, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorInfo': {
            backgroundColor: 'rgba(59, 130, 246, 0.12)',
            borderColor: 'rgba(59, 130, 246, 0.25)',
          },
        },
      },
    },
  },
};

// ============================================================================
// Create and Export Theme
// ============================================================================

export const twilightTheme = createTheme(twilightThemeOptions);

export default twilightTheme;
